import { useState, useCallback } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { TabRow } from './TabRow'
import { MIN_VISIBLE_TABS, type BottomTabItem } from './tab-defaults'

interface TabEditorSectionProps {
  tabItems: BottomTabItem[]
  onTabItemsChange: (items: BottomTabItem[]) => void
  navStyle: string
  isLoading?: boolean
}

export function TabEditorSection({
  tabItems,
  onTabItemsChange,
  navStyle,
  isLoading,
}: TabEditorSectionProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showMinWarning, setShowMinWarning] = useState(false)

  const isVisible = navStyle === 'bottom-tab-bar'
  const visibleCount = tabItems.filter((t) => t.visible).length

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = tabItems.findIndex((t) => t.id === active.id)
      const newIndex = tabItems.findIndex((t) => t.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      onTabItemsChange(arrayMove(tabItems, oldIndex, newIndex))
    },
    [tabItems, onTabItemsChange]
  )

  function handleToggleVisibility(id: string) {
    const item = tabItems.find((t) => t.id === id)
    if (!item) return

    // Trying to hide when at minimum
    if (item.visible && visibleCount <= MIN_VISIBLE_TABS) {
      setShowMinWarning(true)
      setTimeout(() => setShowMinWarning(false), 3000)
      return
    }

    onTabItemsChange(
      tabItems.map((t) => (t.id === id ? { ...t, visible: !t.visible } : t))
    )
  }

  function handleUpdateItem(id: string, updates: Partial<BottomTabItem>) {
    onTabItemsChange(
      tabItems.map((t) => (t.id === id ? { ...t, ...updates } : t))
    )
  }

  if (isLoading) {
    return (
      <div>
        <div className="mb-3 text-[13px] font-semibold text-gray-500">แถบเมนูด้านล่าง</div>
        <div className="flex flex-col gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-12 w-full animate-pulse rounded-sm bg-gray-100 motion-reduce:animate-none"
            />
          ))}
        </div>
      </div>
    )
  }

  if (!isVisible) return null

  return (
    <div>
      <div className="mb-3 text-[13px] font-semibold text-gray-500">แถบเมนูด้านล่าง</div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tabItems.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col gap-0.5">
            {tabItems.map((item) => (
              <TabRow
                key={item.id}
                item={item}
                isExpanded={expandedId === item.id}
                onToggleExpand={() =>
                  setExpandedId((prev) => (prev === item.id ? null : item.id))
                }
                onUpdate={(updates) => handleUpdateItem(item.id, updates)}
                onToggleVisibility={() => handleToggleVisibility(item.id)}
                canHide={item.visible ? visibleCount > MIN_VISIBLE_TABS : true}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Min-3 warning */}
      {showMinWarning && (
        <p className="mt-2 text-xs text-amber-600 animate-in fade-in duration-150">
          ต้องแสดงอย่างน้อย 3 แท็บ
        </p>
      )}
    </div>
  )
}
